import {
  Checkbox,
  FormControlLabel,
  Grid2 as Grid,
  Icon,
  List,
  ListItem,
  ListItemText,
  ListSubheader,
  MenuItem,
  TextField,
  Typography,
} from '@mui/material';
import React, { FC, useState } from 'react';
import { formatDate } from '../Formatters';
import CheckBoxIcon from '@mui/icons-material/CheckBox';
import NotInterestedIcon from '@mui/icons-material/NotInterested';
import CropSquareIcon from '@mui/icons-material/CropSquare';
import { revisionTypeToDisplayText } from './RevisionModel';
import { useMutation } from '@apollo/client';
import { UPDATE_TASK, UPDATE_TYPE } from './revisions.graphql';
import { Revision } from './Details';
import { RevisionType } from '../__generated__/graphql';
import { StyledPaper } from '../theme';
import { StyledAnchor } from '../Components';
import { Unpacked } from '../graphQLTypes/types';

export const Checklist: FC<{ revision: Revision; history: boolean }> = ({ revision, history }) => {
  const [updateType] = useMutation(UPDATE_TYPE);
  const typeOptions = getRevisionTypeOptions();

  const renderItems = (revision: Revision) => {
    if (!revision?.checklist) {
      return null;
    }

    const updatedMilestones = [...(revision.checklist.milestones ?? [])]
      .sort((a, b) => {
        if (!a || !b) return 0;
        return a.number - b.number;
      })
      .map((m, i) => {
        return { ...m, number: i + 1 };
      });

    return updatedMilestones
      .filter((x) => !!x)
      .map((x) => {
        return (
          <Grid key={`grid-${x.name}`} size={{ xs: 12 }}>
            <List
              key={x.name}
              dense
              subheader={
                <ListSubheader sx={{ backgroundColor: 'rgba(255,255,255,0.9)' }}>
                  {x.number} - {x.name} ({x.completedTasks}/{x.numberOfTasks})
                </ListSubheader>
              }>
              {x.tasks
                ?.filter((x) => !!x)
                .map((y) => {
                  return <TaskItem key={y.id} revisionId={revision.id} history={history} task={y} />;
                })}
            </List>
          </Grid>
        );
      });
  };

  const handleChangeType = (event: React.ChangeEvent<HTMLInputElement>) => {
    let value = event.target.value;
    updateType({ variables: { input: { id: revision?.id, type: value as RevisionType } } });
  };

  const checklist = revision?.checklist;
  return (
    <StyledPaper>
      <Grid container justifyContent="space-between">
        <Grid>
          <Typography variant="h5" component="h3">
            Sjekkliste
          </Typography>
          <Grid container alignItems="center">
            <Typography variant="caption">Versjon {checklist?.version}</Typography>
            {checklist?.latestVersion !== checklist?.version && (
              <>
                <Icon color="error" sx={{ fontSize: '1em', marginLeft: 15, marginRight: 5 }}>
                  warning
                </Icon>
                <Typography variant="caption">
                  <StyledAnchor href="https://byggforsk.atlassian.net/wiki/spaces/FOU/pages/61276291" target="_blank" rel="noopener noreferrer">
                    Ny versjon {checklist?.latestVersion} foreligger
                  </StyledAnchor>
                </Typography>
              </>
            )}
          </Grid>
        </Grid>
        <Grid>
          {history ? (
            'Type revisjon:' + revisionTypeToDisplayText(revision?.type)
          ) : (
            <TextField select label="Revisjonstype" value={revision?.type} onChange={handleChangeType}>
              {typeOptions.map((option) => (
                <MenuItem key={option.value} value={option.value}>
                  {option.label}
                </MenuItem>
              ))}
            </TextField>
          )}
        </Grid>
      </Grid>
      <List>
        <Grid container>{renderItems(revision)}</Grid>
      </List>
    </StyledPaper>
  );
};

type ChecklistTask = Unpacked<NonNullable<NonNullable<Unpacked<NonNullable<NonNullable<Revision>['checklist']>['milestones']>>['tasks']>>;
const TaskItem: FC<{ revisionId: string; task: ChecklistTask; history: boolean }> = ({ revisionId, task, history }) => {
  const [checked, setChecked] = useState(!!task?.completed);
  const [notRelevant, setNotRelevant] = useState(!!task?.notRelevant);

  const [updateTask] = useMutation(UPDATE_TASK);
  const doUpdateTask = (done: boolean, irrelevant: boolean = false) =>
    updateTask({ variables: { input: { id: revisionId, taskId: task?.id, done, notRelevant: irrelevant } } });

  const handleToggle = async () => {
    const val = !checked;
    setChecked(val);
    await doUpdateTask(val);
  };

  const handleToggleNotRelevant = async () => {
    const val = !notRelevant;
    setChecked(val);
    setNotRelevant(val);
    await doUpdateTask(val, val);
  };

  const setTaskIcons = (irrelevant: boolean) => {
    return irrelevant ? <NotInterestedIcon color="disabled" /> : <CheckBoxIcon color="disabled" />;
  };

  const setIrrelevantTaskIcons = (irrelevant: boolean) => {
    return irrelevant ? <CheckBoxIcon color="disabled" /> : <CropSquareIcon color="disabled" />;
  };

  if (!task) return null;

  return (
    <Grid container justifyContent="space-between" wrap="nowrap">
      <ListItem key={task.id} sx={{ paddingTop: 1, paddingBottom: 1 }}>
        <FormControlLabel
          control={
            history ? (
              setTaskIcons(notRelevant)
            ) : (
              <Checkbox
                color="primary"
                onChange={handleToggle}
                checked={checked && !notRelevant}
                indeterminate={notRelevant}
                disabled={notRelevant}
              />
            )
          }
          label={''}
        />
        <ListItemText
          primary={
            <Typography variant="subtitle1" color={notRelevant ? 'textSecondary' : 'initial'}>
              {task.name}
            </Typography>
          }
          secondary={task.completed ? `${formatDate(task.completedAt)} - ${task.completedBy?.name}` : ''}
        />
      </ListItem>
      <FormControlLabel
        control={
          history ? setIrrelevantTaskIcons(notRelevant) : <Checkbox onChange={handleToggleNotRelevant} checked={notRelevant} color="primary" />
        }
        label={
          <Typography variant="body2" color="textSecondary" noWrap>
            Ikke relevant
          </Typography>
        }
      />
    </Grid>
  );
};

function getRevisionTypeOptions(): { label: string | null; value: RevisionType }[] {
  return Object.keys(RevisionType)
    .filter((x) => x !== 'Correction')
    .map((x) => {
      const item = RevisionType[x as keyof typeof RevisionType];
      return { value: item, label: revisionTypeToDisplayText(item) };
    });
}
